/**
 * Customizable document naming (Document Naming under Company Preferences).
 *
 * Tracx generates a fixed set of load documents (Customer Confirmation, Driver
 * Confirmation, BOL, …). An org can rename any of them to match its own
 * business vocabulary — e.g. BX Freight renames "Customer Confirmation" to
 * "Delivery Confirmation" so it reads as a POD on freight-collect loads.
 *
 * This module is the single source of truth for:
 *   - the internal document CODE (never changes — used by routes, storage,
 *     template resolution, EDI classification), and
 *   - the default Tracx display NAME (the fallback shown when an org has not
 *     configured a custom name).
 *
 * The custom (per-org) display name is stored in the `organization_document_names`
 * mapping table and resolved server-side; the internal code stays constant so
 * nothing downstream (permissions, template keys, links) ever keys off a name.
 *
 * Shared so the client can render the config grid / menu labels against the
 * same canonical list the server seeds into `document_types`.
 */
/** Internal document codes. Stable identifiers — do NOT rename these strings. */
export const DOCUMENT_TYPE_CODES = [
    'customer_confirmation',
    'driver_confirmation',
    'bill_of_lading',
    'carrier_confirmation',
    'rate_confirmation',
    'delivery_receipt',
    'invoice',
    'work_sheet',
    'settlement',
];
/**
 * Canonical registry. `defaultName` values are the Tracx defaults surfaced in
 * the config grid, PDF titles, menu labels, and download / attachment
 * filenames whenever an org has not set a custom name.
 *
 * NOTE: `customer_confirmation`'s default is "Customer Confirmation" (the V1
 * label users know). Historically the generated PDF banner read "Order
 * Confirmation"; wiring the title through this registry aligns the banner with
 * the label everywhere, and orgs that prefer the old wording can set it as
 * their custom name.
 */
export const DOCUMENT_TYPES = [
    { code: 'customer_confirmation', defaultName: 'Customer Confirmation', category: 'load', sortOrder: 10 },
    { code: 'driver_confirmation', defaultName: 'Driver Confirmation', category: 'load', sortOrder: 20 },
    { code: 'carrier_confirmation', defaultName: 'Carrier Confirmation', category: 'load', sortOrder: 30 },
    { code: 'bill_of_lading', defaultName: 'Bill of Lading', category: 'load', sortOrder: 40 },
    { code: 'rate_confirmation', defaultName: 'Rate Confirmation', category: 'load', sortOrder: 50 },
    { code: 'delivery_receipt', defaultName: 'Delivery Receipt', category: 'load', sortOrder: 60 },
    { code: 'work_sheet', defaultName: 'Work Sheet', category: 'load', sortOrder: 70 },
    { code: 'invoice', defaultName: 'Invoice', category: 'billing', sortOrder: 80 },
    { code: 'settlement', defaultName: 'Settlement', category: 'billing', sortOrder: 90 },
];
/** code → default name, for O(1) fallback lookups. */
export const DOCUMENT_TYPE_DEFAULT_NAMES = DOCUMENT_TYPES.reduce((acc, d) => {
    acc[d.code] = d.defaultName;
    return acc;
}, {});
/** Whether a string is a known internal document code. */
export function isDocumentTypeCode(value) {
    return typeof value === 'string' && DOCUMENT_TYPE_CODES.includes(value);
}
/**
 * Resolve the effective display name for a document code given an org's custom
 * overrides. Falls back to the Tracx default when no (non-empty) custom name is
 * configured. Central fallback rule — server and client both use it.
 */
export function resolveDocumentTypeName(code, customNames) {
    const custom = customNames?.[code];
    if (typeof custom === 'string' && custom.trim().length > 0)
        return custom.trim();
    return DOCUMENT_TYPE_DEFAULT_NAMES[code] ?? code;
}
//# sourceMappingURL=document-types.js.map